'use client'

import { Pip } from '@/design-system'
import { useMockPhase } from './hooks'

const MONO: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 500 }
const LABEL: React.CSSProperties = {
  ...MONO,
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--fg-mute)'
}

const FILES = [
  { name: 'contract-signed.pdf', meta: 'PDF · 240 KB', color: 'var(--sky)' },
  { name: 'final-selects.zip', meta: 'ZIP · 1.2 GB', color: 'var(--acid)' },
  { name: 'notes.txt', meta: 'TXT · 4 KB', color: 'var(--yellow)' }
] as const

interface ReceiveMockProps {
  /** When set, the mock is scrubbed externally instead of running its own time loop. */
  phase?: number
}

export function ReceiveMock({ phase: controlledPhase }: ReceiveMockProps = {}) {
  const loopPhase = useMockPhase(7000, 0.85, controlledPhase !== undefined)
  const phase = controlledPhase ?? loopPhase
  const copyClicked = phase > 0.15 && phase < 0.3
  const showCopied = phase > 0.2 && phase < 0.55
  const filesIn = phase > 0.55
  const file2 = phase > 0.65
  const file3 = phase > 0.75
  const visible = [filesIn, file2, file3]
  const count = visible.filter(Boolean).length

  return (
    <div role="img" aria-label="Animation: request link with files arriving" className="st-receive-grid">
      <div
        style={{
          border: 'var(--border-w) solid var(--ink)',
          boxShadow: 'var(--shadow-lg)',
          background: 'var(--paper-3)'
        }}>
        <div style={{ background: 'var(--ink)', color: 'var(--paper)', padding: '8px 12px' }}>
          <span style={{ ...MONO, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            You · request
          </span>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ ...LABEL, marginBottom: 6 }}>FROM</div>
          <input
            readOnly
            value="brand@studio.co"
            aria-hidden
            tabIndex={-1}
            style={{
              width: '100%',
              border: '2px solid var(--ink)',
              padding: '8px 10px',
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              marginBottom: 10,
              background: 'var(--paper)'
            }}
          />
          <div style={{ ...LABEL, marginBottom: 6 }}>MESSAGE</div>
          <textarea
            readOnly
            value="Final selects + signed contract, please."
            aria-hidden
            tabIndex={-1}
            style={{
              width: '100%',
              border: '2px solid var(--ink)',
              padding: '8px 10px',
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              marginBottom: 12,
              background: 'var(--paper)',
              resize: 'none',
              height: 56
            }}
          />
          <div
            aria-hidden
            style={{
              border: '2px solid var(--ink)',
              padding: '10px 12px',
              background: copyClicked ? 'var(--acid)' : 'var(--yellow)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'background .15s',
              transform: copyClicked ? 'translate(2px, 2px)' : 'none',
              boxShadow: copyClicked ? '2px 2px 0 0 var(--ink)' : 'var(--shadow-sm)'
            }}>
            <span style={{ ...MONO, fontSize: 12, fontWeight: 700, flex: 1 }}>snap.tf/req/oak-river-22</span>
            <span
              style={{ ...MONO, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              {showCopied ? '✓ Copied' : 'Copy'}
            </span>
          </div>
          <div
            style={{
              ...MONO,
              fontSize: 10,
              opacity: 0.6,
              marginTop: 8,
              letterSpacing: '0.12em',
              textTransform: 'uppercase'
            }}>
            No signup required for sender
          </div>
        </div>
      </div>

      <div
        style={{
          border: 'var(--border-w) solid var(--ink)',
          boxShadow: 'var(--shadow-lg)',
          background: 'var(--paper-3)',
          position: 'relative'
        }}>
        <div
          style={{
            background: 'var(--mauve)',
            borderBottom: '2.5px solid var(--ink)',
            padding: '8px 12px'
          }}>
          <span style={{ ...MONO, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            Inbox · live
          </span>
        </div>
        <div style={{ padding: 16, minHeight: 240 }}>
          {!filesIn ? (
            <div
              style={{
                border: '3px dashed var(--ink)',
                padding: 24,
                textAlign: 'center',
                background: 'var(--paper-2)'
              }}>
              <div
                style={{
                  ...MONO,
                  fontSize: 11,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  opacity: 0.6,
                  marginBottom: 6
                }}>
                Waiting
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800 }}>0 files received</div>
            </div>
          ) : (
            <div>
              <div
                style={{
                  ...MONO,
                  fontSize: 11,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  opacity: 0.6,
                  marginBottom: 10
                }}>
                {count} of 3 · From j.client@gmail.com
              </div>
              {FILES.map((f, i) => (
                <div
                  key={f.name}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '28px 1fr auto',
                    gap: 10,
                    alignItems: 'center',
                    padding: '8px 10px',
                    border: '2px solid var(--ink)',
                    marginBottom: 6,
                    background: 'var(--paper)',
                    opacity: visible[i] ? 1 : 0,
                    transform: visible[i] ? 'translateX(0)' : 'translateX(-8px)',
                    transition: 'all .3s'
                  }}>
                  <span style={{ width: 22, height: 22, border: '2px solid var(--ink)', background: f.color }} />
                  <div>
                    <div style={{ ...MONO, fontSize: 11, fontWeight: 700 }}>{f.name}</div>
                    <div style={{ ...MONO, fontSize: 10, opacity: 0.6 }}>{f.meta}</div>
                  </div>
                  <Pip bg="var(--acid)" style={{ fontSize: 9, padding: '2px 6px' }}>
                    NEW
                  </Pip>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
